import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { preferencesApi, type MeasurementSystem } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";

const keyFor = (userId: string | null) => ["player-ui-preferences", userId ?? "guest"];

export function useDistanceUnits() {
  const { user, loading } = useAuth();
  const userId = user?.uid ?? null;
  const client = useQueryClient();
  const preferences = useQuery({
    queryKey: keyFor(userId),
    enabled: !loading && userId !== null,
    queryFn: async () => {
      const response = await preferencesApi.get();
      if (response.user_id !== userId) throw new Error("Preference identity mismatch");
      return response;
    },
    staleTime: 60_000,
  });
  const mutation = useMutation({
    mutationFn: async ({ owner, system }: { owner: string; system: MeasurementSystem }) => {
      const response = await preferencesApi.setMeasurementSystem(system);
      if (response.user_id !== owner) throw new Error("Preference identity mismatch");
      return response;
    },
    onSuccess: async (response, variables) => {
      const queryKey = keyFor(variables.owner);
      await client.cancelQueries({ queryKey, exact: true });
      client.setQueryData(queryKey, response);
    },
  });
  const measurementSystem: MeasurementSystem = preferences.data?.preferences.measurement_system === "metric" ? "metric" : "imperial";
  return {
    measurementSystem,
    preferences,
    saving: mutation.isPending,
    saveError: mutation.isError,
    setMeasurementSystem: (system: MeasurementSystem) => userId
      ? mutation.mutateAsync({ owner: userId, system })
      : Promise.reject(new Error("Sign in to save preferences")),
  };
}
